import * as d3 from 'd3';
import { clamp, startCase } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { DataTableContext } from 'components/DataTable';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlHorizonDamage,
  type AtlasGqlHorizonDamagePropagation,
  AtlasGqlBladeSide,
} from 'types/atlas-graphql';

import { Axes } from './Axes';
import { type ColorCodingAttribute, BladeSide } from './BladeDamagePlotContainer';
import { EdgeShape } from './EdgeShape';
import { type PlotTooltipProps, PlotTooltip } from './PlotTooltip';
import { type ColorCodingMap, mapEdgeDamagesToPoints, mapSideDamagesToPoints } from './pointUtils';
import { type SelectionRangeProps, SelectionRange } from './SelectionRange';
import { SideShape } from './SideShape';

type BladeDamagePlotProps = {
  bladeSide: BladeSide;
  visible: boolean;
  damages: AtlasGqlHorizonDamage[];
  propagations: AtlasGqlHorizonDamagePropagation[];
  damageColumns: TTableColumnDef[];
  loading: boolean;
  shapeWidths: number[];
  bladeLength: number;
  xAxisMax?: number; // if provided, x-axis range will be 0 - xAxisMax, otherwise 0 - bladeLength
  yAxisMax?: number; // if provided, y-axis range will be 0 - yAxisMax, otherwise 0 - max(shapeWidths)
  colorCodingAttribute?: ColorCodingAttribute;
  colorCodingMap?: ColorCodingMap;
  allowAreaSelection: boolean;
  onAreaSelection: (params: {
    bladeSide: BladeSide | null;
    distanceRange: [number | null, number | null] | null;
    chordRange?: [number, number] | null;
  }) => void;
  isInternalBladeInspection?: boolean;
  observationId?: string;
};

const [width, height] = [1000, 100];
const [marginTop, marginRight, marginBottom, marginLeft] = [2, 2, 20, 20];

export const BladeDamagePlot: React.FunctionComponent<BladeDamagePlotProps> = ({
  bladeSide,
  visible,
  damages = [],
  propagations = [],
  damageColumns,
  loading,
  shapeWidths,
  bladeLength,
  xAxisMax,
  yAxisMax,
  colorCodingAttribute,
  colorCodingMap,
  allowAreaSelection,
  onAreaSelection,
  isInternalBladeInspection,
  observationId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const plotRef = useRef<SVGSVGElement>(null);
  const brushRef = useRef<SVGGElement>(null);
  const [selectionRange, setSelectionRange] =
    useState<Pick<SelectionRangeProps, 'distance' | 'chord' | 'rawSelection'>>();
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [tooltipState, setTooltipState] =
    useState<Pick<PlotTooltipProps, 'damages' | 'position'>>();

  const { filteredInfo } = useContext(DataTableContext);

  useEffect(() => {
    // Clear selection range element when corresponding filter is cleared
    if (!allowAreaSelection) {
      setSelectionRange(undefined);
    }
  }, [allowAreaSelection]);

  const showTooltip = !allowAreaSelection || selectionRange;

  // Projects a set of blade shape widths by percent radial distance to [x,y] coordinate pairs
  const { shapePoints, maxBladeWidth }: { shapePoints: [number, number][]; maxBladeWidth: number } =
    useMemo(() => {
      const maxBladeWidth = yAxisMax ?? Math.max(...shapeWidths);
      return {
        shapePoints: [BladeSide.LeadingEdge, BladeSide.TrailingEdge].includes(bladeSide)
          ? []
          : shapeWidths.map((w, i) => [(i * bladeLength) / 100, (100 * w) / maxBladeWidth]),
        maxBladeWidth,
      };
    }, [shapeWidths, bladeSide, yAxisMax]);

  // Create d3 scales for radial distance (x) and chord % (y)
  const xScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, xAxisMax ?? bladeLength])
        .range([marginLeft, width - marginRight]),
    [bladeLength, xAxisMax, width]
  );
  const yScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, 100])
        .range(
          bladeSide === BladeSide.SuctionSide
            ? [marginTop, height - marginBottom]
            : [height - marginBottom, marginTop]
        ),
    [bladeSide, height]
  );

  useEffect(() => {
    // Given distance and chord (if applicable) ranges, returns corresponding d3 brush selection
    const getSelectionFromDistanceAndChord = ({
      distance,
      chord,
    }: {
      distance: [number, number];
      chord?: [number, number];
    }): [[number, number], [number, number]] => {
      const selectionDistance = distance.map(xScale) as [number, number];

      const yRange = yScale.range();
      const [_yMin, _yMax] = chord
        ? (chord.map(yScale) as [number, number])
        : [yRange[0], yRange[1]];

      const yMin = bladeSide === BladeSide.PressureSide ? _yMax : _yMin;
      const yMax = bladeSide === BladeSide.PressureSide ? _yMin : _yMax;

      return [
        [selectionDistance[0], yMax],
        [selectionDistance[1], yMin],
      ];
    };

    if (filteredInfo?.['Blade Side']?.length === 1 && filteredInfo?.Distance?.[0]?.length === 2) {
      const bladeSideFilter = filteredInfo['Blade Side'][0];

      if (bladeSideFilter === bladeSide) {
        const _distanceFilter = filteredInfo.Distance[0];
        const distanceFilter: [number, number] = [
          Math.max(_distanceFilter[0] ?? 0, 0),
          Math.min(_distanceFilter[1] ?? bladeLength, bladeLength),
        ];

        const _chordFilter = filteredInfo.Chord?.[0];
        const chordFilter: [number, number] | undefined =
          [AtlasGqlBladeSide.PressureSide, AtlasGqlBladeSide.SuctionSide]
            .map(startCase)
            .includes(bladeSideFilter) && _chordFilter?.length === 2
            ? [Math.max(_chordFilter[0], 0), Math.min(_chordFilter[1], 100)]
            : undefined;

        const selection = getSelectionFromDistanceAndChord({
          distance: distanceFilter,
          chord: chordFilter,
        });

        setSelectionRange({
          distance: distanceFilter,
          chord: chordFilter,
          rawSelection: selection,
        });
      } else {
        setSelectionRange(undefined);
      }
    } else {
      setSelectionRange(undefined);
    }
  }, [xScale, yScale, bladeSide, filteredInfo]);

  // Once the brush element exists, set up its handler function
  useEffect(() => {
    if (brushRef.current) {
      // Returns the width of the blade at a given distance (for calculating chord)
      const getWidthAtDistance = (distance: number): number =>
        shapeWidths[Math.round((100 * distance) / bladeLength)];

      const xRange = xScale.range();
      const yRange = yScale.range();

      // Given a d3 brush selection, returns corresponding distance and chord ranges
      const getDistanceAndChordRange = (
        selection: d3.BrushSelection
      ): {
        distance: [number, number];
        chord?: [number, number];
      } => {
        const [[distanceMin, _yMin], [distanceMax, _yMax]] = (
          selection as [[number, number], [number, number]]
        ).map(([x, y]) => [
          clamp(xScale.invert(x), 0, bladeLength),
          clamp(yScale.invert(y), 0, 100),
        ]);

        // account for inverted y-axis on pressure side
        const yMin = bladeSide === BladeSide.PressureSide ? _yMax : _yMin;
        const yMax = bladeSide === BladeSide.PressureSide ? _yMin : _yMax;

        if ([BladeSide.PressureSide, BladeSide.SuctionSide].includes(bladeSide)) {
          // Calculate chord range from y-axis pixel range and the blade width arrays
          const [widthAtDistanceMin, widthAtDistanceMax] = [distanceMin, distanceMax].map(
            getWidthAtDistance
          );

          const chordMin =
            clamp(
              Math.min(
                yMin / (widthAtDistanceMin / maxBladeWidth),
                yMin / (widthAtDistanceMax / maxBladeWidth)
              ),
              0,
              100
            ) || 0; // 0 fallback prevents setting to NaN at edge of svg
          const chordMax =
            clamp(
              Math.max(
                yMax / (widthAtDistanceMin / maxBladeWidth),
                yMax / (widthAtDistanceMax / maxBladeWidth)
              ),
              0,
              100
            ) || 100; // 100 fallback prevents setting to NaN at edge of svg

          return {
            distance: [distanceMin, distanceMax],
            chord: [chordMin, chordMax],
          };
        } else {
          return {
            distance: [distanceMin, distanceMax],
          };
        }
      };

      /**
       * Handler for area selection: gets the distance and chord (if PS or SS) range of the selection
       * and passes these, along with the blade side, to the area selection handler function in the container
       */
      const areaSelection = d3
        .brush()
        .extent([
          [xRange[0], yRange[bladeSide === BladeSide.SuctionSide ? 0 : 1]],
          [xRange[1], yRange[bladeSide === BladeSide.SuctionSide ? 1 : 0]],
        ])
        /**
         * As the user drags the selection, calculate the distance/chord ranges to render and where to render
         * them (either top-right or top-left) corner of the selected area.
         */
        .on('brush', ({ selection }: d3.D3BrushEvent<unknown>) => {
          if (selection) {
            const { distance, chord } = getDistanceAndChordRange(selection);

            setSelectionRange({
              distance,
              chord,
              rawSelection: selection as [[number, number], [number, number]],
            });
          } else {
            setSelectionRange(undefined);
          }
        })
        /**
         * When the user finishes selection, calculate the distance/chord ranges to apply as filters for
         * the plot and the table.
         */
        .on('end', ({ selection }: d3.D3BrushEvent<unknown>) => {
          if (selection) {
            const { distance, chord } = getDistanceAndChordRange(selection);

            onAreaSelection({
              bladeSide,
              distanceRange: [
                distance[0] === 0 ? null : distance[0],
                distance[1] >= bladeLength ? null : distance[1],
              ],
              chordRange: chord,
            });
          } else {
            onAreaSelection({
              bladeSide: null,
              distanceRange: null,
              chordRange: null,
            });
          }
        });

      d3.select<SVGElement, unknown>(brushRef.current).call(areaSelection);
    }
  }, [allowAreaSelection, brushRef.current, xScale, yScale, bladeSide, damages]);

  /**
   * Map damages with distance and chord attributes to svg shapes at the correct [x,y] coordinates.
   * Any damages without distance, and any PS/SS damages without chord will be omitted.
   * (see ./pointUtils for detail)
   */
  const damagePoints: JSX.Element[] = useMemo(
    () =>
      damages
        .map(
          [BladeSide.LeadingEdge, BladeSide.TrailingEdge].includes(bladeSide)
            ? mapEdgeDamagesToPoints({
                xScale,
                yScale,
                colorCodingAttribute,
                colorCodingMap,
                propagations,
                selectedDamageIds: showTooltip ? tooltipState?.damages?.map(d => d.id) : [],
                theme,
              })
            : mapSideDamagesToPoints({
                widths: shapeWidths,
                maxWidth: maxBladeWidth,
                length: bladeLength,
                xScale,
                yScale,
                colorCodingAttribute,
                colorCodingMap,
                propagations,
                selectedDamageIds: showTooltip ? tooltipState?.damages?.map(d => d.id) : [],
                theme,
                isInternalBladeInspection,
                observationId,
              })
        )
        .filter((point): point is JSX.Element => !!point),
    [
      damages,
      propagations,
      bladeSide,
      xScale,
      yScale,
      colorCodingAttribute,
      colorCodingMap,
      showTooltip,
      tooltipState,
      shapeWidths,
      maxBladeWidth,
      bladeLength,
      theme,
    ]
  );

  // get damages in range
  const getTooltipDamages = useCallback(
    (position?: { x: number; y: number }) => {
      if (position) {
        // get position
        const { x, y } = position;

        // get range
        const xRange = { min: x - 5, max: x + 5 };
        const yRange = { min: y - 5, max: y + 5 };
        const [domainMin, domainMax] = xScale.domain();

        // find points in range
        const damagesInRange = damagePoints
          .filter(point => {
            const { x: xPoint, y: yPoint, damageId } = point.props;
            if (xPoint < xRange.min || xPoint > xRange.max) {
              return false;
            }

            // out-of-range damages are pinned to root/tip — group them by x only
            const damage = damages.find(d => d.id === damageId);
            const distance = damage?.primaryObservationGroupAttrs?.Distance;
            const isOutOfRange =
              Number.isFinite(distance) && (distance < domainMin || distance > domainMax);

            return isOutOfRange || (yPoint >= yRange.min && yPoint <= yRange.max);
          })
          .map(point => {
            const damageId = point.props.damageId;
            return damages.find(d => d.id === damageId);
          })
          .filter((d): d is AtlasGqlHorizonDamage => !!d);

        return damagesInRange;
      }

      return [];
    },
    [damages, damagePoints, xScale]
  );

  // handler for damage tooltip position
  useEffect(() => {
    if (plotRef.current) {
      const svg = d3.select(plotRef.current);

      // Hover events
      svg.on('mousemove', event => {
        // set initial tootip properties
        if (!tooltipOpen) {
          // get the mouse position relative to the SVG
          const [x, y] = d3.pointer(event);

          // get damages in range
          const damages = getTooltipDamages({ x, y });

          // set damages
          if (damages.length > 0) {
            setTooltipState({
              damages,
              position: { x, y },
            });
          }
        }
      });
    }
  }, [tooltipOpen, getTooltipDamages]);

  // handler for damage tooltip damages
  useEffect(() => {
    // update tooltip properties
    if (tooltipOpen && tooltipState) {
      // get existing damages and position
      const { damages, position } = tooltipState;

      // get damages for tooltip
      const updatedDamages = getTooltipDamages(position);

      // update damages
      if (updatedDamages.length !== damages?.length) {
        setTooltipState({
          damages: updatedDamages,
          position,
        });
      }
    }
  }, [tooltipOpen, tooltipState, getTooltipDamages]);

  return (
    <svg
      ref={plotRef}
      key={bladeSide}
      viewBox={`0 0 ${width} ${height}`}
      className={visible ? '' : 'hidden'}
      opacity={loading || !!damagePoints.length ? 1 : 0.5}
    >
      {!!shapePoints.length &&
      [BladeSide.PressureSide, BladeSide.SuctionSide].includes(bladeSide) ? (
        <SideShape
          xScale={xScale}
          yScale={yScale}
          points={shapePoints}
          bladeSide={bladeSide}
          isInternalBladeInspection={isInternalBladeInspection}
        />
      ) : (
        <EdgeShape />
      )}
      {!isInternalBladeInspection && (
        <Axes
          xScale={xScale}
          yScale={yScale}
          bladeSide={bladeSide}
          maxWidth={maxBladeWidth}
          height={height}
        />
      )}
      <g fill="none" stroke="currentColor" strokeWidth={`${isInternalBladeInspection ? 1.2 : 1.5}`}>
        {...damagePoints}
      </g>
      {!isInternalBladeInspection && (
        <g transform={`translate(54, 12)`} fill={theme.labelColor}>
          <text
            style={{
              fontSize: '10px',
              textAnchor: 'middle',
            }}
          >
            {bladeSide}
          </text>
        </g>
      )}
      {!loading && !damagePoints.length && (
        <g transform={`translate(${width / 2}, 46)`} fill={theme.plot.axisColor}>
          <text
            style={{
              fontSize: '14px',
              textAnchor: 'middle',
            }}
          >
            {t('table.noData', 'No Data')}
          </text>
        </g>
      )}
      {allowAreaSelection && <g className="brush" ref={brushRef} />}
      {selectionRange && (
        <SelectionRange
          {...selectionRange}
          xScale={xScale}
          yScale={yScale}
          shapePoints={shapePoints}
          bladeLength={bladeLength}
          bladeSide={bladeSide}
        />
      )}
      {showTooltip && (
        <PlotTooltip
          {...tooltipState}
          damageColumns={damageColumns}
          onChange={setTooltipOpen}
          isInternalBladeInspection={isInternalBladeInspection}
        />
      )}
    </svg>
  );
};
